import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { askQuestion, evidenceLine, shareText, winMeta, winReason, winTitle } from "@/helpers/resultsCopy";
import WantListingThumb from "@/components/WantListingThumb";
import { wantListingLinkProps } from "@/helpers/wantListingLink";
import { updateWin } from "@/services/resultsService";
import { askWhen, byNewest, winsSummary } from "./winsTable";
import "./ResultsWins.scss";

const glue = (text) => (text ? text.replace(/Want-Listing/g, "Want-⁠Listing") : text);

export const isQuestion = (win) => win.agentStatus === "asked" || (win.confidence === "possible" && win.agentStatus === "auto");

const Chevron = () => (
  <svg className="results-wins__chevron" viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
    <path d="M6 3.5 10.5 8 6 12.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const AskRow = ({ win, networkName, onAction, busy }) => (
  <li className="results-ask">
    <p className="results-ask__text">
      <span className="visually-hidden">{askQuestion(win, networkName)} </span>
      <span className="results-ask__address" aria-hidden="true">
        {win.address || glue(winTitle(win))}
      </span>
      <span className="results-ask__when">{askWhen(win)}</span>
    </p>
    <div className="results-ask__actions">
      <button type="button" className="btn btn-primary results-ask__btn" disabled={busy} onClick={() => onAction(win, "yes")}>
        Yes
      </button>
      <button type="button" className="btn btn-tertiary results-ask__btn" disabled={busy} onClick={() => onAction(win, "no")}>
        No
      </button>
    </div>
  </li>
);

const WinDetail = ({ win, networkName, onAction, busy }) => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const events = (win.evidence || []).map(evidenceLine).filter(Boolean);
  const canShare = win.source === "bls" && win.address;

  const share = async () => {
    try {
      await navigator.clipboard.writeText(shareText(win, networkName));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="results-wins__detail-grid">
      {events.length ? (
        <ol className="results-wins__timeline">
          {events.map((event, index) => (
            <li key={`${event.text}-${index}`}>
              <span className="results-wins__when">{event.when}</span>
              <span className="results-wins__event">{glue(event.text)}</span>
            </li>
          ))}
        </ol>
      ) : null}
      <div className="results-wins__why">
        {win.wantListing?.id ? (
          <a className="results-wins__listing" {...wantListingLinkProps(navigate, win.wantListing)}>
            <WantListingThumb
              map={win.wantListing.map}
              width={64}
              height={40}
              title={win.wantListing.title || "Buyer Want-Listing"}
            />
            <span className="results-wins__listing-text">
              <span className="results-wins__listing-label">Buyer Want-&#8288;Listing</span>
              <span className="results-wins__listing-title">{win.wantListing.title || "View Buyer Want-Listing"}</span>
            </span>
          </a>
        ) : null}
        <p className="results-wins__reason">{glue(winReason(win, networkName))}</p>
        <div className="results-wins__actions">
          {canShare ? (
            <button type="button" className="results-wins__link" onClick={share}>
              {copied ? "Copied" : "Copy to share"}
            </button>
          ) : null}
          <button type="button" className="results-wins__link is-quiet" disabled={busy} onClick={() => onAction(win, "remove")}>
            {win.source === "gitcha" ? "Not from Gitcha" : `Not a ${networkName} BLS win`}
          </button>
        </div>
      </div>
    </div>
  );
};

export const useWinActions = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ win, action }) => updateWin(win.id, action),
    onMutate: async ({ win, action }) => {
      await queryClient.cancelQueries({ queryKey: ["results", "wins"] });
      const previous = queryClient.getQueryData(["results", "wins"]);
      queryClient.setQueryData(["results", "wins"], (old) => {
        if (!old) return old;
        const next = (old.wins || [])
          .map((item) => (item.id === win.id && action === "yes" ? { ...item, agentStatus: "confirmed" } : item))
          .filter((item) => !(item.id === win.id && (action === "remove" || action === "no")));
        return { ...old, wins: next };
      });
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(["results", "wins"], context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["results", "wins"] }),
  });
  return { onAction: (win, action) => mutation.mutate({ win, action }), busy: mutation.isPending };
};

const DealItem = ({ win, networkName, onAction, busy }) => {
  const [open, setOpen] = useState(false);
  const detailId = `results-deal-${win.id}`;
  return (
    <li className={`results-deal${open ? " is-open" : ""}`}>
      <button type="button" className="results-deal__toggle" aria-expanded={open} aria-controls={detailId} onClick={() => setOpen((value) => !value)}>
        <Chevron />
        <span className="results-deal__text">
          <span className="results-deal__title">{glue(winTitle(win))}</span>
          <span className="results-deal__meta">{winMeta(win)}</span>
        </span>
      </button>
      <div className="results-deal__detail" id={detailId} hidden={!open}>
        <WinDetail win={win} networkName={networkName} onAction={onAction} busy={busy} />
      </div>
    </li>
  );
};

const DealGroup = ({ label, wins, networkName, onAction, busy }) =>
  wins.length ? (
    <div className="results-wins-tile__group">
      {label ? <p className="results-wins-tile__group-label">{label}</p> : null}
      <ul className="results-wins-tile__deals">
        {wins.map((win) => (
          <DealItem key={win.id} win={win} networkName={networkName} onAction={onAction} busy={busy} />
        ))}
      </ul>
    </div>
  ) : null;

const gitchaLine = (wins) => {
  const clients = wins.filter((win) => win.kind === "client").length;
  const deals = wins.length - clients;
  return [clients ? `${clients} new ${clients === 1 ? "client" : "clients"}` : null, deals ? `${deals} ${deals === 1 ? "deal" : "deals"}` : null]
    .filter(Boolean)
    .join(" and ");
};

export const WinsTile = ({ data, networkName = "LAR", open = false, onToggle }) => {
  const { onAction, busy } = useWinActions();
  const wins = data?.wins || [];
  const questions = wins.filter(isQuestion);
  const counted = wins.filter((win) => !isQuestion(win)).sort(byNewest);
  const bls = counted.filter((win) => win.source !== "gitcha");
  const gitcha = counted.filter((win) => win.source === "gitcha");
  const figures = Object.fromEntries(winsSummary(bls).map((item) => [item.key, item.value]));
  const ask = questions[0];
  const hasVolume = figures.volume && figures.volume !== "$0";
  const [street, ...place] = (ask?.address || "").split(",");
  const listId = "results-wins-tile-deals";

  return (
    <section className={`results-card results-wins-tile${open ? " is-open" : ""}`} aria-labelledby="results-wins-tile-title">
      <div className="results-wins-tile__head">
        <p id="results-wins-tile-title" className="results-wins-tile__title">
          BLS wins
        </p>
        {counted.length || questions.length > 1 ? (
          <button type="button" className="results-wins-tile__toggle" aria-expanded={open} aria-controls={listId} onClick={onToggle}>
            {open ? "Hide deals" : "See all deals"}
          </button>
        ) : null}
      </div>
      {bls.length ? (
        <>
          <p className="results-wins-tile__big">
            {hasVolume ? figures.volume : figures.wins}
            <span className="results-wins-tile__unit">{hasVolume ? " closed" : figures.wins === 1 ? " win" : " wins"}</span>
          </p>
          <p className="results-wins-tile__line">
            {[hasVolume ? `${figures.wins} ${figures.wins === 1 ? "deal" : "deals"}` : null, figures.contract ? `${figures.contract} under contract` : null]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </>
      ) : (
        <p className="results-wins-tile__line">
          Deals that close after the {networkName} BLS connects you show&nbsp;here.
        </p>
      )}
      {gitcha.length ? <p className="results-wins-tile__gitcha">Gitcha.com: {gitchaLine(gitcha)}</p> : null}
      {ask ? (
        <div className="results-wins-tile__ask" role="group" aria-label={askQuestion(ask, networkName)}>
          <p className="results-wins-tile__question">Did the {networkName} BLS help with this&nbsp;deal?</p>
          <div className="results-wins-tile__row">
            <div className="results-wins-tile__deal">
              <p className="results-wins-tile__address">{ask.address ? street : glue(winTitle(ask))}</p>
              <p className="results-wins-tile__when">{[place.join(",").trim().replace(/ (\S+)$/, "\u00a0$1"), askWhen(ask)].filter(Boolean).join(" · ")}</p>
            </div>
            <div className="results-wins-tile__answers">
              <button type="button" className="btn btn-primary results-ask__btn" disabled={busy} onClick={() => onAction(ask, "yes")}>
                Yes
              </button>
              <button type="button" className="btn btn-tertiary results-ask__btn" disabled={busy} onClick={() => onAction(ask, "no")}>
                No
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {open ? (
        <div className="results-wins-tile__list" id={listId}>
          {questions.length > 1 ? (
            <div className="results-wins-tile__group">
              <p className="results-wins-tile__group-label">Also needs your answer</p>
              <ul className="results-asks">
                {questions.slice(1).map((win) => (
                  <AskRow key={win.id} win={win} networkName={networkName} onAction={onAction} busy={busy} />
                ))}
              </ul>
            </div>
          ) : null}
          <DealGroup label={gitcha.length ? `${networkName} BLS` : null} wins={bls} networkName={networkName} onAction={onAction} busy={busy} />
          <DealGroup label="Gitcha.com" wins={gitcha} networkName={networkName} onAction={onAction} busy={busy} />
          <p className="results-wins-tile__note">Found in MLS records. Remove any that did not come from the {networkName}&nbsp;BLS.</p>
        </div>
      ) : null}
    </section>
  );
};

export default WinsTile;
